import { toast } from 'react-toastify';

import useSyncMutation from '../useSyncMutation';

import { useLanguageContext } from '~providers';
import { postRequest } from '~services';
import { MutateContext } from '~types';
import { getPaymentMutationErrorMessage } from '~utils/payment.util';

type AddPaymentMethodRequest = {
  paymentMethodId: string;
};

/**
 * Adds a payment method to the user's account after a successful Stripe
 * SetupIntent confirmation (`POST api/payment/add-payment-method`, ENG-7877).
 * Replaces the legacy `update-payment-method` upsert for the new flow.
 */
export const useMutationAddPaymentMethod = ({
  shouldToast = false,
  onErrorCallback,
}: MutateContext & { onErrorCallback?: (errorMsg: string) => void } = {}) => {
  const { lt } = useLanguageContext();

  return useSyncMutation(
    async (request: AddPaymentMethodRequest): Promise<string> => {
      const response = await postRequest<AddPaymentMethodRequest>({
        path: 'api/payment/add-payment-method',
        data: request,
      });

      return response.data.id;
    },
    {
      onError(error) {
        const message = getPaymentMutationErrorMessage({
          error,
          fallbackTextId: 'text.hooks.useMutationAddPaymentMethod.error',
          fallbackText: 'Something went wrong when adding payment method',
          lt,
        });

        if (shouldToast) {
          toast.error(message);
        }

        onErrorCallback?.(message);
      },
    },
  );
};
